<template>
  <label
    class="pdl-toggle-switch inline-flex items-center relative align-middle gap-2"
    :class="className"
    :qaid="qaidValue"
  >
    <input
      ref="input"
      type="checkbox"
      class="absolute opacity-0 w-px h-px"
      :name="name"
      :checked="modelValue"
      @change="toggle"
      @input="inputEvent"
    />
    <div class="pdl-toggle-switch-core block relative m-0">
      <div class="pdl-toggle-switch-button block absolute overflow-hidden" />
    </div>
    <span :class="{'sr-only': hideLabels}">
      <template v-if="labels">
        <span class="pdl-toggle-switch-label" :class="labelClass" v-html="toggled ? labelChecked : labelUnchecked" />
      </template>
      <span :class="labelClass" v-if="textLabel">{{ textLabel }}</span>
    </span>
  </label>
</template>

<script>
const constants = {
  labelChecked: 'on',
  labelUnchecked: 'off',
};

const contains = (object, title) => {
  return typeof object === 'object' && Object.prototype.hasOwnProperty.call(object, title);
};

const px = (v) => v + 'px';

export default {
  name: 'PdlToggleSwitch',
  inheritAttrs: false,

  props: {
    /**
     * Use a monochrome color style.
     */
    monochrome: {
      type: Boolean,
      default: false,
    },
    /**
     * Position the text label to the left of the switch.
     */
    labelLeft: {
      type: Boolean,
      default: false,
    },
    /**
     * Initial state of the toggle switch.
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable the switch.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Name to attach to the generated input field.
     */
    name: {
      type: String,
      default: '',
    },
    /**
     * If set to `true`, will be watching changes in `value` property and overwrite the current state of the switch whenever `value` prop changes.
     */
    sync: {
      type: Boolean,
      default: false,
    },
    /*eslint-disable */
    /**
     * If `String`, color of the switch when checked.<br/>If `Object`, colors for the switch when checked/unchecked or disabled. Example: `{checked: '#00FF00', unchecked: '#FF0000', disabled: '#CCCCCC'}`
     */
    color: {
      type: [String, Object],
      validator(value) {
        return typeof value === 'object'
          ? value.checked || value.unchecked || value.disabled
          : typeof value === 'string';
      },
    },
    /**
     * QAID to attach to the generated input field.
     */
    qaid: {
      type: String,
      default: '',
    },
    /*eslint-enable */
    /**
     * If `Boolean`, shows/hides default labels ("on" and "off").<br/>If `Object`, sets custom labels for both states.<br />Example: `{checked: 'Foo', unchecked: 'Bar'}`
     */
    labels: {
      type: [Boolean, Object],
      default: false,
      validator(value) {
        return typeof value === 'object' ? value.checked || value.unchecked : typeof value === 'boolean';
      },
    },
    /**
     * Text label
     */
    textLabel: {
      type: String,
      default: '',
    },
    /**
     * Text label classes
     */
    labelClass: {
      type: String,
      default: '',
    },

    hideLabels: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      toggled: !!this.modelValue,
    };
  },

  emits: ['update:modelValue'],

  computed: {
    className() {
      let {toggled, disabled, monochrome, labelLeft} = this;
      return [
        {
          toggled,
          disabled,
          'pdl-toggle-switch--monochrome': monochrome,
          'flex-row-reverse': labelLeft,
        },
      ];
    },
    qaidValue() {
      return `${this.qaid}${this.toggled ? '-toggled' : ''}${this.disabled ? '-disabled' : ''}`;
    },

    labelChecked() {
      return contains(this.labels, 'checked') ? this.labels.checked : constants.labelChecked;
    },
    labelUnchecked() {
      return contains(this.labels, 'unchecked') ? this.labels.unchecked : constants.labelUnchecked;
    },
  },

  watch: {
    modelValue(value) {
      if (this.sync) {
        this.toggled = !!value;
      }
    },
  },

  mounted() {
    if (
      (this.labels === false || (typeof this.labels === 'object' && !this.labels.checked && !this.labels.unchecked)) &&
      (!this.textLabel || this.textLabel.length < 1)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[PdlToggleSwitch] For accessibility, provide a textLabel or set labels to true or a custom object.'
      );
    }
  },

  methods: {
    toggle(event) {
      this.toggled = !this.toggled;
      /**
       * Input event for v-model.
       */
      this.$emit('update:modelValue', this.toggled);
      /**
       * Emitted when the state of the toggle switch changes.<br/>`value` - state of the object<br />`srcEvent` - source click event
       */
      this.$emit('change', {
        value: this.toggled,
        srcEvent: event,
      });
    },

    inputEvent() {
      this.$emit('update:modelValue', this.toggled);
    },
  },
};
</script>
<style lang="scss">
@import 'ToggleSwitch.scss';
</style>
